/**
 * GroupSessionAnalysisSection (v2.0)
 *
 * Session-level sentiment analysis for a `category='group_transcript'`
 * governance document. Rendered inside DocumentDetailPanel above the
 * SpeakerMappingPanel; no-op for any other category.
 *
 * Flow: the consultant maps speakers to personas (panel below), then runs
 * "Analyze Session" — one server-side NLP pass over all mapped speakers'
 * statements relabeled to persona names, persisted per document. "View
 * Session Analysis" opens the same SentimentDashboard the 1:1 interview
 * modal uses; "Export PDF" downloads the anonymized persona-level report
 * (no speaker names — the feedback artifact promised to interviewees).
 *
 * Error surfacing follows SpeakerMappingPanel: the server's 422/502 message
 * is shown verbatim (e.g. "Azure OpenAI not configured…"), never masked.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SentimentDashboard } from '../SentimentDashboard';
import { useApiClient } from '../../hooks/useApiClient';

interface SpeakerSummary {
  id: string;
  persona_id: string | null;
  statement_count: number;
}

interface PillarCell {
  overall_sentiment: number;
  by_persona?: Record<string, number>;
  quotes: Array<{ text: string; sentiment: number; persona?: string }>;
}

interface SessionAnalysis {
  document_id: string;
  emotion_scores: Record<string, number>;
  fair_heatmap: { by_pillar: Record<string, PillarCell> };
  statement_count: number;
  analyzed_at: string;
}

interface AnalyzeSessionResponse {
  analysis: {
    emotions: { overall_emotion: string; emotion_scores: Record<string, number> };
    fair_heatmap: { by_pillar: Record<string, PillarCell> };
  };
  statement_count: number;
  analyzed_at: string;
  warnings?: { unmapped_speakers: number; excluded_statements: number };
}

interface GroupSessionAnalysisSectionProps {
  documentId: string;
  documentName: string;
  /** Renders only when the parent document's category is 'group_transcript'. */
  category: string;
}

function biggestPillar(
  heatmap: SessionAnalysis['fair_heatmap'],
  direction: 'pain' | 'win',
): string {
  const entries = Object.entries(heatmap.by_pillar);
  if (entries.length === 0) return direction === 'pain' ? 'Findable' : 'Accessible';
  const sorted = entries.sort((a, b) =>
    direction === 'pain'
      ? a[1].overall_sentiment - b[1].overall_sentiment
      : b[1].overall_sentiment - a[1].overall_sentiment,
  );
  return sorted[0][0];
}

/** Strongest emotion in the stored scores — the session-level "overall". */
function dominantEmotion(scores: Record<string, number>): string {
  const entries = Object.entries(scores);
  if (entries.length === 0) return 'neutral';
  return entries.sort((a, b) => b[1] - a[1])[0][0];
}

export const GroupSessionAnalysisSection: React.FC<GroupSessionAnalysisSectionProps> = ({
  documentId,
  documentName,
  category,
}) => {
  // Destructure the stable function refs — the client OBJECT is a new
  // reference every render, so depending on it from useCallback loops the
  // load effect (same pattern as SpeakerMappingPanel).
  const { get, post, authFetch } = useApiClient();

  const [speakers, setSpeakers] = useState<SpeakerSummary[]>([]);
  const [analysis, setAnalysis] = useState<SessionAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const isGroupTranscript = category === 'group_transcript';

  const load = useCallback(async () => {
    if (!isGroupTranscript) return;
    setLoading(true);
    setError(null);
    try {
      const speakersRes = await get<SpeakerSummary[]>(
        `/api/group-transcripts/${documentId}/speakers`,
        { skipCache: true },
      );
      setSpeakers(Array.isArray(speakersRes) ? speakersRes : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSpeakers([]);
    }
    try {
      const analysisRes = await get<SessionAnalysis>(
        `/api/group-transcripts/${documentId}/analysis`,
        { skipCache: true },
      );
      setAnalysis(analysisRes);
    } catch {
      // 404 NOT_ANALYZED is the normal "not yet" state — not an error.
      setAnalysis(null);
    }
    setLoading(false);
  }, [get, documentId, isGroupTranscript]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setError(null);
    setWarning(null);
    try {
      const result = await post<AnalyzeSessionResponse>(
        `/api/group-transcripts/${documentId}/analyze-session`,
      );
      setAnalysis({
        document_id: documentId,
        emotion_scores: result.analysis.emotions.emotion_scores,
        fair_heatmap: result.analysis.fair_heatmap,
        statement_count: result.statement_count,
        analyzed_at: result.analyzed_at,
      });
      if (result.warnings) {
        setWarning(
          `${result.warnings.unmapped_speakers} unmapped speaker(s) excluded ` +
            `(${result.warnings.excluded_statements} statement(s)). Map them below and re-analyze to include them.`,
        );
      }
      // Mapping/statement counts may have shifted server-side; refresh quietly.
      void load();
    } catch (err) {
      // Server message passes through verbatim (422 NO_MAPPED_SPEAKERS,
      // 502 ANALYSIS_FAILED with the real upstream cause).
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAnalyzing(false);
    }
  }, [post, documentId, load]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await authFetch(
        `/api/group-transcripts/${documentId}/analysis/report?format=pdf`,
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: { message?: string } }).error?.message ||
            `Failed to export PDF (HTTP ${response.status})`,
        );
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'Group_Session_Analysis.pdf';
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }, [authFetch, documentId]);

  if (!isGroupTranscript) return null;

  const mappedCount = speakers.filter(s => s.persona_id !== null).length;

  return (
    <section className="doc-panel__section gsa-section">
      <div className="doc-panel__section-header">
        <h3>Session Analysis</h3>
        {analysis && (
          <span className="gsa-analyzed-badge">
            Analyzed {new Date(analysis.analyzed_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {loading ? (
        <div className="doc-panel__loading">Loading session analysis...</div>
      ) : (
        <>
          <p className="gsa-status-line">
            {speakers.length === 0
              ? 'No speakers parsed from this transcript yet.'
              : `${mappedCount} of ${speakers.length} speaker(s) mapped to personas.`}
            {' '}Session analysis aggregates sentiment at the persona level — no individual is named.
          </p>

          {error && <div className="doc-panel__error gsa-error">{error}</div>}
          {warning && <div className="gsa-warning">{warning}</div>}

          <div className="gsa-actions">
            <button
              className="btn btn--primary"
              onClick={handleAnalyze}
              disabled={analyzing || speakers.length === 0}
              data-testid="analyze-session-btn"
            >
              {analyzing
                ? 'Analyzing session…'
                : analysis
                  ? 'Re-analyze Session'
                  : 'Analyze Session'}
            </button>
            {analysis && (
              <>
                <button
                  className="btn btn--secondary"
                  onClick={() => setShowModal(true)}
                  data-testid="view-session-analysis-btn"
                >
                  View Session Analysis
                </button>
                <button
                  className="btn btn--secondary"
                  onClick={handleExportPdf}
                  disabled={exporting}
                  data-testid="export-session-pdf-btn"
                >
                  {exporting ? 'Exporting…' : 'Export PDF'}
                </button>
              </>
            )}
          </div>
        </>
      )}

      {showModal && analysis && (
        <div className="gsa-overlay" onClick={() => setShowModal(false)}>
          <div className="gsa-modal" onClick={e => e.stopPropagation()}>
            <div className="gsa-modal__header">
              <div>
                <h2>Session Analysis: {documentName}</h2>
                <span className="gsa-modal__meta">
                  {mappedCount} mapped speaker(s) ·{' '}
                  {new Set(speakers.filter(s => s.persona_id).map(s => s.persona_id)).size}{' '}
                  persona(s) · persona-level feedback, no names
                </span>
              </div>
              <div className="gsa-modal__actions">
                <button
                  className="gsa-modal__export"
                  onClick={handleExportPdf}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting…' : 'Export PDF'}
                </button>
                <button
                  className="gsa-modal__close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="gsa-modal__body">
              <SentimentDashboard
                sentiment={{
                  emotions: {
                    overall_emotion: dominantEmotion(analysis.emotion_scores),
                    emotion_scores: analysis.emotion_scores,
                  },
                  fair_heatmap: {
                    by_pillar: Object.fromEntries(
                      Object.entries(analysis.fair_heatmap.by_pillar).map(([pillar, cell]) => [
                        pillar,
                        {
                          overall_sentiment: cell.overall_sentiment,
                          by_persona: cell.by_persona ?? {},
                          quotes: cell.quotes,
                        },
                      ]),
                    ),
                  },
                  insights: {
                    biggest_pain_point: biggestPillar(analysis.fair_heatmap, 'pain'),
                    biggest_win: biggestPillar(analysis.fair_heatmap, 'win'),
                  },
                  metadata: {
                    total_statements: analysis.statement_count,
                    analyzed_at: analysis.analyzed_at,
                    model: 'session-analysis',
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GroupSessionAnalysisSection;
